import React, { useState } from "react";
import styles from "./GenericTable.module.scss";
import map from "lodash/map";
import { useTranslation } from "react-i18next";
import { getPercentageParts } from "../../../utils/agcUtil";
import { CurrencyInputFormatter } from "../../../utils/currencyFormatter";
import Icon from "../Icon/Icon";
import { useAppSelector } from "../../../redux/hooks";
import dropdownArrowLight from "../../../assets/images/LightTheme/down_arrow_light.svg";
import dropdownArrowDark from "../../../assets/images/DarkTheme/down_arrow_dark.svg";
import { isEmpty } from "lodash";
import { NumberFormatter } from "../../../utils/NumberFormatter";
import parse from 'html-react-parser'
import { countryLocaleMappings } from "../../../constants/dataConstants";

interface GenericTableProps {
  tableData: any;
  kpiCode: any;
  highlightColumns?: number[];
  highlightRows?: number[];
  affiliateCode?: string;
  groupedColumnClass?: boolean;
  emptyValuePlaceholder?: string;
  currency?: string;
}

const GenericTable: React.FC<GenericTableProps> = ({
  tableData,
  kpiCode,
  highlightColumns,
  highlightRows,
  affiliateCode,
  groupedColumnClass,
  emptyValuePlaceholder = "---",
  currency
}) => {
  const { t, i18n } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const isHighlighted = (cIdx: number) => highlightColumns?.includes(cIdx);

  const ArrowIcon: React.FC<{ up: boolean }> = ({ up }) =>
    up ? (
      <Icon
        name="up_filled_arrow_table"
        size={14}
        color={"var(--tag-tag-text-green, #B3D67B)"}
        className={styles.search_icon}
      />
    ) : (
      <Icon
        name="down_filled_arrow_table"
        size={14}
        color={"var(--tag-tag-text-red, #F16A81)"}
        className={styles.search_icon}
      />
    );

  const resolveCurrencyCode = (cell: any): string => {
    return cell?.kpiCurrency || tableData?.kpiCurrency || currency || "USD";
  };

  const renderCell = (
    cell: any,
    cIdx: number,
    specificPlaceholder?: string
  ): React.ReactNode => {
    const placeholder =
      specificPlaceholder !== undefined
        ? specificPlaceholder
        : emptyValuePlaceholder;
    const val = cell?.kpiValue;
    if (val === undefined || val === null || val === "---") return placeholder;

    // Try to translate only first column as it may be the header or a special label
    if (cIdx === 0) {
      const translated = i18n.exists(
        `charts_tables_dynamic_labels.${cell?.kpiValue}`
      )
        ? t(`charts_tables_dynamic_labels.${cell?.kpiValue}`)
        : null;

      if (translated) {
        return <span>{translated}</span>;
      }
    }

    if (cell?.kpiDataType === "percentage") {
      const percentageParts = getPercentageParts(val);
      const numb = typeof val === "string" ? parseFloat(val) : Number(val);
      const showArrow =
        isHighlighted(cIdx) &&
        percentageParts.isDefined &&
        !percentageParts.isZero &&
        Number.isFinite(numb);
      const up = Number(numb) > 0;
      const absVal = typeof percentageParts.abs === "number"
          ? percentageParts.abs
          : Math.abs(Number(numb));
      const locale = countryLocaleMappings[affiliateCode ?? "010"] || "en-US";
      const formattedAbsVal = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(absVal);
      const showYoY = cell?.yoy === "STLY";

      return (
        <span className={styles.cell_with_arrow}>
          {showArrow && <ArrowIcon up={up} />}
          <span>{formattedAbsVal}%</span>
          {showYoY && (
            <span style={{ color: "var(--text-text-primary)" }}>
              {"(STLY)"}
            </span>
          )}
        </span>
      );
    }

    if (cell?.kpiDataType === "currency") {
      const numb = typeof val === "string" ? parseFloat(val) : Number(val);
      if (!Number.isFinite(numb)) return placeholder;
      const currencyCode = resolveCurrencyCode(cell);
      const formatted = CurrencyInputFormatter(currencyCode, Math.abs(numb));

      if (isHighlighted(cIdx) && numb !== 0) {
        return (
          <span className={styles.cell_with_arrow}>
            <ArrowIcon up={numb > 0} /> {formatted}
          </span>
        );
      }
      return formatted;
    }

    if (cell?.kpiDataType === "number") {
      const numb = typeof val === "string" ? parseInt(val) : Number(val);
      if (!Number.isFinite(numb)) return placeholder;
      const formatted = NumberFormatter(affiliateCode ?? "010", numb);
      if (isHighlighted(cIdx) && numb !== 0) {
        return (
          <span className={styles.cell_with_arrow}>
            <ArrowIcon up={numb > 0} /> {formatted}
          </span>
        );
      }

      return formatted;
    }

    if (isHighlighted(cIdx) && typeof val === "number" && val !== 0) {
      const formattedValue = NumberFormatter(
        affiliateCode ?? "US",
        Math.abs(val)
      );

      return (
        <span className={styles.cell_with_arrow}>
          <ArrowIcon up={val > 0} /> {formattedValue}
        </span>
      );
    }

    if (cell?.kpiDataType === "number") {
      const formattedValue = NumberFormatter(
        affiliateCode ?? "US",
        Math.abs(val)
      );

      return <span>{formattedValue}</span>;
    }

    return val as React.ReactNode;
  };

  const getCellClass = (
    cell: any,
    cIdx: number,
    nextRow: any[] | null = null
  ): string => {
    const currentRaw = cell?.kpiValue;
    const currentVal =
      typeof currentRaw === "number" ? currentRaw : Number(currentRaw);

    // Omit first column from highlighting as it contains headers
    if (nextRow && cIdx > 0 && currentRaw) {
      const nextRaw = nextRow[cIdx]?.kpiValue;
      const nextVal = typeof nextRaw === "number" ? nextRaw : Number(nextRaw);

      if (
        Number.isFinite(currentVal) &&
        Number.isFinite(nextVal) &&
        currentVal < nextVal
      ) {
        return styles.negative;
      }
    }

    if (!isHighlighted(cIdx) || !Number.isFinite(currentVal)) {
      return "";
    }

    if (currentVal === 0) {
      return styles.yoy_value;
    }

    return currentVal > 0 ? styles.positive : styles.negative;
  };

  const GrouppedRow = ({
    cell,
    rIdx,
    rowClass,
  }: {
    cell: any;
    rIdx: number;
    rowClass: any;
  }) => {
    const dropdownArrow =
      currentTheme === "light" ? dropdownArrowLight : dropdownArrowDark;
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => {
      setExpanded((prev) => !prev);
    };
    // Determine if parent is even or odd
    const isParentEven = rowClass === styles.even_row;

    const yearlyTotalIndices =
      tableData?.header?.row?.reduce((acc: number[], col: any, idx: number) => {
        const key = (col?.kpiValue ?? "").toString().replace(/\s+/g, "");
        if (
          ["yearlyTotal"].includes(key)
        ) {
          acc.push(idx);
        }
        return acc;
      }, []) ?? [];

    const filteredChildren = cell[0].children?.filter((child: any[]) => {
      const isRetailProfit = child[0]?.kpiValue === "retailProfit";

      if (!isRetailProfit || yearlyTotalIndices.length === 0) return true;

      return yearlyTotalIndices.some((index: number) => {
        const val = parseFloat(child[index]?.kpiValue);
        return !isNaN(val) && val !== 0;
      });
    });

    const colCount = tableData?.header?.row?.length || cell.length;
    const columns = Array.from({ length: colCount });

    return (
      <>
        <tr className={`${styles.tbl_row} ${rowClass}`}>
          {columns.map((_, cIdx) => {
            const cellData = cell[cIdx];
            if (cIdx === 0) {
              return (
                <td key={`${rIdx}-${cIdx}`}>
                  <span className={styles.group_head} onClick={toggleExpanded}>
                    <img
                      src={dropdownArrow}
                      alt="Downkey"
                      className={`${styles.arrow_icon_row} ${
                        expanded ? styles.rotate : ""
                      }`}
                    />
                    <span className={styles.category_name}>
                      {cellData?.kpiValue}
                    </span>
                  </span>
                </td>
              );
            }
            return (
              <td
                key={`${rIdx}-${cIdx}`}
                className={getCellClass(cellData, cIdx)}
              >
                {renderCell(cellData, cIdx)}
              </td>
            );
          })}
        </tr>
        {expanded &&
          filteredChildren &&
          !isEmpty(filteredChildren) &&
          filteredChildren?.map((child: any[], childrowIdx: number) => {
            const lastYrRowData =
              kpiCode === "business_line" &&
              filteredChildren &&
              filteredChildren[childrowIdx + 2]
                ? filteredChildren[childrowIdx + 2]
                : null;
            // Calculate child row class: First 2 rows opposite of parent, then 2 rows same as parent, repeating
            const pairIndex = Math.floor(childrowIdx / 2);
            const shouldBeOpposite = pairIndex % 2 === 0;
            const childRowClass = (
              shouldBeOpposite ? !isParentEven : isParentEven
            )
              ? styles.even_row
              : styles.odd_row;
            return (
              <tr
                key={`${rIdx}-${childrowIdx}`}
                className={`${styles.tbl_row} ${
                  groupedColumnClass ? rowClass : childRowClass
                } ${styles.tbl_grp_row}`}
              >
                {columns.map((_, cIdx) => {
                  const cellData = child[cIdx];
                  return (
                    <td
                      key={`${rIdx}-${childrowIdx}-${cIdx}`}
                      className={getCellClass(cellData, cIdx, lastYrRowData)}
                    >
                      {renderCell(cellData, cIdx, "---")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
      </>
    );
  };

  return (
    <div className={styles.annual_tbl_wrapper}>
      <table className={styles.annual_table}>
        <thead>
          <tr className={styles.tbl_head}>
            {map(tableData?.header?.row, (col: any, idx: number) => {
              const key = (col?.kpiValue ?? idx).toString().replace(/\s+/g, "");
              const translated = i18n.exists(
                `charts_tables_dynamic_labels.${key}`
              )
                ? t(`charts_tables_dynamic_labels.${key}`)
                : col?.kpiValue;

              return <th key={key}>{parse(translated)}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {map(tableData?.body?.rows, (row: any[], rIdx: number) => {
            // Skip rows that are undefined, null, or have no first element
            if (!row || !Array.isArray(row) || row.length === 0 || !row[0]) {
              return null;
            }

            const rowClass = rIdx % 2 === 0 ? styles.even_row : styles.odd_row;

            if (row[0].children) {
              return (
                <GrouppedRow
                  key={rIdx}
                  cell={row}
                  rIdx={rIdx}
                  rowClass={rowClass}
                />
              );
            }

            const nextRow = highlightRows?.includes(rIdx)
              ? tableData?.body?.rows?.[rIdx + 1]
              : null;

            return (
              <tr key={rIdx} className={`${styles.tbl_row} ${rowClass}`}>
                {map(row, (cell: any, cIdx: number) => (
                  <td
                    key={`${rIdx}-${cIdx}`}
                    className={getCellClass(cell, cIdx, nextRow)}
                  >
                    {renderCell(cell, cIdx)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default GenericTable;
